import { createContext, useContext } from 'react';
import { FunnelDetailDto, FunnelVariableDto } from 'api/funnel';
import { ExpressionVariable } from './components/expression/ExpressionBuilder';

export interface FunnelDetailContextValue {
  tunnelId: string;
  funnel: FunnelDetailDto;
  /**
   * Variables du tunnel, chargées par une route dédiée.
   *
   * `GET /tunnels/{id}` renvoie bien un tableau `variables`, mais le back ne le remplit
   * pas : l'ancien back-office appelait déjà `variablesValuesByContextNoPagedResult`.
   * S'appuyer sur le détail donnait donc une liste vide, et surtout aucune variable
   * proposée dans les formules de tarification.
   */
  variables: FunnelVariableDto[];
  /** Les mêmes, réduites à ce dont le constructeur de formule a besoin. */
  expressionVariables: ExpressionVariable[];
  /** Recharge le tunnel ; à appeler après toute mutation qui touche son détail. */
  reloadFunnel: () => Promise<void>;
  /** Recharge les variables ; à appeler après modification de l'une d'elles. */
  reloadVariables: () => Promise<void>;
  /**
   * Jeton d'invalidation de la tarification.
   *
   * Le prix est calculé par le back à partir des garanties, des frais et de la config
   * parc : toute mutation de l'un d'eux le périme. Le bloc Tarification possède sa
   * propre requête, que rien ne relançait — le prix affiché restait celui d'avant la
   * modification jusqu'au prochain rechargement de page.
   */
  pricingVersion: number;
  /** À appeler après toute mutation qui change le prix. */
  invalidatePricing: () => void;
}

const FunnelDetailContext = createContext<FunnelDetailContextValue | undefined>(undefined);

export const FunnelDetailProvider = FunnelDetailContext.Provider;

/**
 * Donne accès au tunnel courant depuis n'importe quel bloc.
 *
 * Évite le prop drilling de l'ancien back-office, où chaque sous-bloc recevait le tunnel,
 * son id et un callback de rafraîchissement en paramètres successifs.
 *
 * Attention : le contenu des modales est rendu hors de ce provider (portail de
 * l'overlay). Les modales reçoivent donc ce dont elles ont besoin en propriétés.
 */
export const useFunnelDetail = (): FunnelDetailContextValue => {
  const context = useContext(FunnelDetailContext);
  if (!context) {
    throw new Error('useFunnelDetail doit être utilisé dans un FunnelDetailProvider');
  }
  return context;
};

/** Branches sur lesquelles le back accepte une configuration de parc immobilier. */
const PARKABLE_BRANCH_CODES = ['GLI', 'CBNO'];

export const isParkableFunnel = (funnel: FunnelDetailDto): boolean =>
  PARKABLE_BRANCH_CODES.includes(funnel.produit?.branche?.code ?? '');
